import clsx from 'clsx';
import { addDays, parseISO } from 'date-fns';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { paths, resultsUrl, searchToParams } from '@/app/paths';
import { useOverlays } from '@/app/state/overlays';
import { useFormat } from '@/app/state/preferences';
import { useVehicleSearch } from '@/app/state/vehicleSearch';
import { Button, Calendar, Icon, Segmented, Sheet, StepButton, Toggle, useToast } from '@/design-system';
import { useT } from '@/i18n';
import { today, toISODate } from '@/lib/clock';
import { addMinutes, billableDays, EXTRA_DAY_GRACE_MINUTES, isLateReturn, stepTime } from '@/lib/rental';
import { track } from '@/services/analytics';
import { ALL_CATEGORIES, type VehicleKind, type VehicleSearch } from '@/services/domain';
import { useVehicleCities, useVehicleSearchResults } from '@/services/queries';
import { useKindOptions } from '../components/labels';
import { messages } from '../i18n';
import styles from './VehicleSearchSheet.module.css';

/**
 * «Où et quand partez-vous ?» (screen 02). Edits a draft; «Afficher …» applies it and opens the results.
 * Validation as on the current site: a return date is required and must be after the pickup date.
 */
export function VehicleSearchSheet() {
  const overlays = useOverlays();
  // Mounted only while open, so each opening starts a fresh draft from the current search.
  return overlays.current === 'vehicleSearch' ? <OpenSheet /> : null;
}

function OpenSheet() {
  const overlays = useOverlays();
  const { search: searchState, update } = useVehicleSearch();
  const [draft, setDraft] = useState<VehicleSearch>(searchState);
  const [end, setEnd] = useState<string | null>(searchState.end);
  const [month, setMonth] = useState(() => parseISO(searchState.start));
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const toast = useToast();
  const t = useT(messages);
  const f = useFormat();
  const kindOptions = useKindOptions();
  const { data: cities } = useVehicleCities(draft.kind);
  const complete = { ...draft, end: end ?? toISODate(addDays(parseISO(draft.start), 1)) };
  const { data: models } = useVehicleSearchResults(complete, {
    category: ALL_CATEGORIES,
    transmission: 'all',
    freeDelivery: false,
    sort: 'price-asc',
  });

  const patch = (p: Partial<VehicleSearch>) => setDraft((d) => ({ ...d, ...p }));
  const minDay = toISODate(today());

  const pick = (day: Date) => {
    const iso = toISODate(day);
    if (end !== null || iso <= draft.start) {
      patch({ start: iso });
      setEnd(null);
      return;
    }
    setEnd(iso);
  };

  const submit = () => {
    if (!end) {
      toast.show(t('reserve.chooseReturn'));
      return;
    }
    const next = { ...draft, end };
    // Search pixel (P9), with the real dates and times of the search.
    track('Search', {
      search_string: next.city,
      content_category: next.kind === 'moto' ? 'motos' : 'voitures', // i18n-ignore: analytics value, kept as before
      city: next.city,
      startDate: next.start,
      returnDate: next.end,
      pickupTime: next.pickupTime,
      returnTime: next.returnTime,
    });
    update(next);
    overlays.close();
    // Pages that read the search from their URL stay where they are; elsewhere the search opens the results.
    if (pathname.startsWith('/listings/listing-details/') || pathname.startsWith('/pages/booking-detail/')) {
      navigate(`${pathname}?${searchToParams(next)}`, { replace: true });
    } else if (pathname.startsWith(paths.results)) {
      // Keep the active filters (category, gearbox, sort…), except a category of the other vehicle kind.
      const keep = new URLSearchParams(search);
      if (next.kind !== searchState.kind) keep.delete('category');
      const params = new URLSearchParams(searchToParams(next));
      keep.forEach((v, k) => {
        if (!params.has(k)) params.set(k, v);
      });
      navigate(`${pathname}?${params.toString()}`, { replace: true });
    } else {
      navigate(resultsUrl(next));
    }
  };

  const late = isLateReturn(draft.pickupTime, draft.returnTime);
  const days = billableDays(draft.start, end ?? complete.end, draft.pickupTime, draft.returnTime);
  const graceAt = addMinutes(draft.pickupTime, EXTRA_DAY_GRACE_MINUTES);
  const count = models?.length;

  return (
    <Sheet
      open
      onClose={overlays.close}
      eyebrow={t('sheet.eyebrow')}
      title={t('sheet.title')}
      bodyClassName={styles.body}
      footer={
        <>
          <div className={styles.sum}>
            <b>
              {f.range(draft.start, end)} · {f.days(days)}
            </b>
            <span className="t-small t-muted">
              {draft.city} · {draft.pickupTime} → {draft.returnTime}
            </span>
          </div>
          <Button onClick={submit}>
            <Icon name="search" size={18} strokeWidth={2.4} />
            {count === undefined ? t('search.submit') : t('sheet.show', { count })}
          </Button>
        </>
      }
    >
      <div className={styles.a}>
        <Segmented
          label={t('kind.label')}
          options={kindOptions}
          value={draft.kind}
          onChange={(kind: VehicleKind) => patch({ kind })}
        />
        <div>
          <span className="t-label">{t('search.place')}</span>
          <div className={styles.cities}>
            {(cities ?? []).map((c) => {
              const on = c.name === draft.city;
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={on}
                  className={clsx(styles.city, on && styles.cityOn)}
                  onClick={() => patch({ city: c.name })}
                >
                  <span className={styles.pin}>
                    <Icon name="pin" size={17} />
                  </span>
                  <span className={styles.cityText}>
                    <b>{c.name}</b>
                    <span>{c.caption}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles.cal}>
        <span className="t-label">{t('search.dates')}</span>
        <Calendar
          month={month}
          onMonthChange={setMonth}
          start={parseISO(draft.start)}
          end={end ? parseISO(end) : null}
          onPick={pick}
          isDisabled={(d) => toISODate(d) < minDay}
          hint={end === null ? t('calendar.chooseReturn') : t('calendar.restart')}
        />
      </div>

      <div className={styles.c}>
        <div>
          <span className="t-label">{t('sheet.hours')}</span>
          <div className={styles.hrs}>
            <TimeBox
              label={t('search.pickup')}
              value={draft.pickupTime}
              onChange={(pickupTime) => patch({ pickupTime })}
            />
            <TimeBox
              label={t('search.return')}
              value={draft.returnTime}
              onChange={(returnTime) => patch({ returnTime })}
            />
          </div>
          <p className={clsx(styles.grace, late ? styles.graceWarn : styles.graceOk)}>
            <Icon name="clock" size={16} strokeWidth={2} />
            <span>{late ? t('grace.late', { time: graceAt }) : t('grace.ok', { time: graceAt })}</span>
          </p>
        </div>
        <div className={styles.delivery}>
          <div>
            <b>{t('sheet.delivery')}</b>
            <span className="t-small t-muted">{t('deliveryHint')}</span>
          </div>
          <Toggle label={t('sheet.delivery')} checked={draft.delivery} onChange={(delivery) => patch({ delivery })} />
        </div>
      </div>
    </Sheet>
  );
}

function TimeBox({
  label,
  value,
  onChange,
}: {
  /** «Départ» / «Retour», translated. */
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const t = useT(messages);
  return (
    <div className={styles.hrBox}>
      <div>
        <small>{label}</small>
        <b>{value}</b>
      </div>
      <div className={styles.st}>
        <StepButton
          size={34}
          direction="down"
          label={t('time.earlier', { label })}
          onClick={() => onChange(stepTime(value, -1))}
        />
        <StepButton
          size={34}
          direction="up"
          label={t('time.later', { label })}
          onClick={() => onChange(stepTime(value, 1))}
        />
      </div>
    </div>
  );
}
